import React from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Element } from 'react-bulma-components'
import { Translate } from '../../utils/translations'
import InputField from './fields/input'
import SelectField from './fields/select'
import { countriesAsSelectioOptions } from '../../constants/countries'
import { campaignApi } from '../../campaigns/api'
import type { EmailConfirmationDto } from '../../api/generated/models'

/**
 * Validation function for email confirmation form
 * Only email and country are required according to the API specification
 */
const validateForm = (values: EmailConfirmationDto) => {
  const errors: any = {}

  if (!values.requestor.email) {
    errors.requestor = { ...errors.requestor, email: <Translate value="fields.required" /> }
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.requestor.email)) {
    errors.requestor = {
      ...errors.requestor,
      email: <Translate value="fields.validations.email" />,
    }
  }

  if (!values.requestor.country) {
    errors.requestor = { ...errors.requestor, country: <Translate value="fields.required" /> }
  }

  return errors
}

/**
 * Build the initial values for the email confirmation form, pre-filling them
 * from previously saved raw values when the user navigates back to this step.
 * @param savedValues - Raw form values saved on a previous visit
 */
const buildInitialValues = (savedValues?: Record<string, any>): EmailConfirmationDto => ({
  requestor: {
    email: savedValues?.requestor?.email ?? '',
    country: savedValues?.requestor?.country ?? '',
    language: '',
  },
})

interface EmailConfirmationFormProps {
  onSuccess?: (result: any) => void
  onError?: (error: any) => void
  language: string
  /** Raw values entered on a previous visit, used to pre-fill the form. */
  savedValues?: Record<string, any>
  /** Persists the raw form values for back/forward navigation. */
  onSaveValues?: (values: Record<string, any>) => void
}

/**
 * Email confirmation form component using Formik
 * Handles the creation of email confirmation entries for basketball hoop recalls
 */
const EmailConfirmationForm: React.FC<EmailConfirmationFormProps> = ({
  onSuccess,
  onError,
  language,
  savedValues,
  onSaveValues,
}) => {
  // Hook for the email confirmation mutation (campaign-specific endpoint)
  const useCreateEmailConfirmation = campaignApi.useCreateEmailConfirmation
  const emailConfirmationMutation = useCreateEmailConfirmation({
    mutation: {
      onSuccess: data => {
        console.log('data', data)
        onSuccess?.(data.data)
      },
      onError: error => {
        onError?.(error)
      },
    },
  })

  /**
   * Handle form submission
   * @param values - Form values from Formik
   * @param actions - Formik actions for form management
   */
  const handleSubmit = async (values: EmailConfirmationDto, actions: any) => {
    try {
      // Persist the raw values so the step is pre-filled when navigating back.
      onSaveValues?.(values)
      await emailConfirmationMutation.mutateAsync({
        data: {
          ...values,
          requestor: { ...values.requestor, language },
        },
      })
      actions.setSubmitting(false)
    } catch (error) {
      actions.setSubmitting(false)
      // Error is handled by the mutation onError callback
    }
  }

  return (
    <Element>
      <Formik
        enableReinitialize
        initialValues={buildInitialValues(savedValues)}
        validate={validateForm}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <Field
              name="requestor.email"
              component={InputField}
              type="email"
              label={<Translate value="fields.email" />}
            />

            <Field
              name="requestor.country"
              component={SelectField}
              label={<Translate value="fields.country" />}
              options={countriesAsSelectioOptions}
              selectDefaultChooseOptionLabel={<Translate value="fields.choose" />}
            />

            <Button
              type="submit"
              color="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              fullwidth
              data-testid="step-submit"
            >
              <Translate value="form.submit" />
            </Button>
          </Form>
        )}
      </Formik>
    </Element>
  )
}

export default EmailConfirmationForm
